import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Le Crès' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries au Crès',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Le Crès' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres au Crès. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous dans les pavillons autour du lac du Crès ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous intervenons dans tout Le Crès, y compris les quartiers résidentiels proches du lac. Les pavillons des années 80-90 sont notre spécialité : volets roulants à rénover, baies coulissantes à reprendre. Délai 24-48h.',
      },
    },
    {
      '@type': 'Question',
      name: 'Pouvez-vous changer les roulettes d\'une baie ancienne au Crès ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolument. Les baies coulissantes installées dans les années 80-90 au Crès ont souvent des roulettes qui n\'ont jamais été changées. Nous remplaçons par des modèles récents, plus fluides et silencieux. Devis gratuit.',
      },
    },
  ],
};

export default function LeCresPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Le Crès | DFPRO"
        description="Baie coulissante ancienne, volet roulant à rénover au Crès ? DFPRO intervient 24-48h, 7j/7. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-le-cres/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante au Crès"
        subtitle="Pavillons des années 80-90, baies à rénover, volets à reprendre : nos techniciens réparent vos menuiseries au Crès. Devis gratuit, 24-48h."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Baie ancienne ou volet en panne au Crès ?"
        ctaText="Devis gratuit, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent au Crès et dans l'est montpelliérain, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          Le Crès, à l'est de Montpellier, compte de nombreux pavillons construits dans les années 80-90. Les menuiseries arrivent en fin de cycle : baies coulissantes aux roulettes usées, volets roulants à motoriser. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/lecres-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante au Crès"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante : roulettes d'origine à changer</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les baies coulissantes installées il y a 30-40 ans au Crès n'ont souvent jamais été entretenues. Les roulettes sont usées jusqu'à la corde et la baie <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coince</Link> ou déraille. Nous remplaçons par des modèles récents, fluides et silencieux.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Roulettes</Link>, <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>, <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link>.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant ancien : rénovation et motorisation</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">volets roulants</Link> des pavillons du Crès méritent souvent une rénovation complète. <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">Blocage</Link>, <Link to="/changer-sangle-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">sangle</Link> craquelée, tablier voilé.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous proposons la <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link> des volets manuels, très demandée. <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">Changement de moteur</Link>, rénovation complète.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/lecres-volet-02.jpg"
              alt="Rénovation de volet roulant au Crès"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/lecres-vitrage-03.jpg"
              alt="Remplacement de vitrage au Crès"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrier et fenêtre : modernisation</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier</Link> pour <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link>, <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">vitrage embué</Link>, <Link to="/remplacement-vitre-cassee-herault/" className="text-accent-500 font-semibold hover:underline">vitre cassée</Link>.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">Fenêtres</Link> : <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémone</Link>, <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link>, <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">oscillo-battant</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention au Crès sous 24 à 48h</h2>
        <p>
          Nous couvrons tout Le Crès : centre-ville, lac, quartiers résidentiels. Communes voisines : <Link to="/depannage-menuiserie-castelnau-le-lez/" className="text-accent-500 font-semibold hover:underline">Castelnau-le-Lez</Link>, <Link to="/depannage-menuiserie-montpellier/" className="text-accent-500 font-semibold hover:underline">Montpellier</Link>, Jacou. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}